import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  MessageCircle, 
  Check,
  ArrowLeft,
  Calendar,
  ChevronRight,
  ClipboardList,
  Star,
  X
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { services, contactInfo } from '../../lib/data';
import PageMeta from '../../components/PageMeta';

const ServicePage = () => {
  const { slug } = useParams();
  const service = services.find(s => s.slug === slug);

  if (!service) {
    return <Navigate to="/services/injury-sports" replace />;
  }

  // Context-specific WhatsApp messages
  const whatsappMessages = {
    'injury-sports': "Hi Ali, I'm interested in Injury & Sports Treatment.",
    'nutritional-therapy': "Hi Ali, I'm interested in your Nutritional Therapy service.",
    'once-off': "Hi Ali, I'd like to enquire about a once-off service.",
    'talks': "Hi Ali, I'd like to enquire about talks or workshops.",
  };
  const whatsappUrl = `https://wa.me/353877727580?text=${encodeURIComponent(whatsappMessages[slug] || "Hi Ali, I'd like to enquire about your services.")}`;

  // Split full description into paragraphs for better readability
  const descriptionParagraphs = service.fullDescription.split('\n\n');

  return (
    <div data-testid={`service-page-${slug}`}>
      <PageMeta
        title={`${service.title} | Ali Dorgan`}
        description={service.shortDescription}
      />
      {/* Hero Section */}
      <section className="py-20 md:py-28 bg-cream-100">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-sage-600 hover:text-sage-700 mb-10 transition-colors group font-medium"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              Back to Home
            </Link>
            
            <div className="max-w-3xl">
              <span className="text-xs uppercase tracking-[0.2em] text-sage-500 font-semibold mb-6 block">
                Services
              </span>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-charcoal leading-tight mb-8">
                {service.title}
              </h1>
              <p className="text-xl text-charcoal/70 leading-relaxed">
                {service.shortDescription}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section — visible within one screen of hero on mobile (skip for injury-sports, has practitioner section) */}
      {service.slug !== 'injury-sports' && (
      <section className="py-10 md:py-14 bg-white" data-testid="service-cta-top">
        <div className="container-custom">
          <div className="max-w-xl mx-auto">
            {service.slug === 'nutritional-therapy' ? (
              <Card className="bg-charcoal border-0 shadow-medium rounded-2xl overflow-hidden">
                <CardContent className="p-8 text-center">
                  <h3 className="font-serif text-2xl font-light text-cream-100 mb-4">
                    Get Started
                  </h3>
                  <p className="text-cream-200/70 mb-8 leading-relaxed">
                    Complete the pre-consultation questionnaire to apply for nutritional therapy.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button
                      asChild
                      size="lg"
                      className="bg-sage-600 hover:bg-sage-700 text-white rounded-full gap-2 py-6 px-8 font-semibold hover:-translate-y-0.5 transition-all duration-300"
                      data-testid="service-apply-btn"
                    >
                      <Link to="/services/nutritional-therapy/apply">
                        <ClipboardList className="w-5 h-5" />
                        Apply for Nutritional Therapy
                      </Link>
                    </Button>
                    <Button
                      asChild
                      size="lg"
                      className="bg-white text-charcoal hover:bg-cream-100 rounded-full py-6 px-8 font-semibold hover:-translate-y-0.5 transition-all duration-300"
                      data-testid="service-whatsapp-btn"
                    >
                      <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                        <MessageCircle className="w-5 h-5 mr-2" />
                        Message Ali
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-charcoal border-0 shadow-medium rounded-2xl overflow-hidden">
                <CardContent className="p-8 text-center">
                  <h3 className="font-serif text-2xl font-light text-cream-100 mb-4">
                    Ready to Book?
                  </h3>
                  <p className="text-cream-200/70 mb-8 leading-relaxed">
                    Get in touch to discuss your needs or book an appointment.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button
                      asChild
                      size="lg"
                      className="bg-[#25D366] hover:bg-[#20BD5C] text-white rounded-full gap-2 py-6 px-8 font-semibold hover:-translate-y-0.5 transition-all duration-300"
                      data-testid="service-whatsapp-btn"
                    >
                      <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                        <MessageCircle className="w-5 h-5" />
                        Message Ali
                      </a>
                    </Button>
                    <Button
                      asChild
                      size="lg"
                      className="bg-white text-charcoal hover:bg-cream-100 rounded-full py-6 px-8 font-semibold hover:-translate-y-0.5 transition-all duration-300"
                      data-testid="service-book-btn"
                    >
                      <Link to="/services/injury-sports#practitioners">
                        <Calendar className="w-5 h-5 mr-2" />
                        Book Injury & Sports Treatment
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>
      )}

      {/* Insurance Info — Injury & Sports only */}
      {service.slug === 'injury-sports' && (
        <section className="py-6 bg-cream-50" data-testid="insurance-section">
          <div className="container-custom">
            <div className="max-w-3xl mx-auto">
              <Card className="border border-sage-200/60 shadow-soft rounded-xl overflow-hidden bg-white">
                <CardContent className="p-6 md:p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-sage-100 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="w-5 h-5 text-sage-600" />
                    </div>
                    <div>
                      <h3 className="font-serif text-lg font-medium text-charcoal mb-3">Health Insurance</h3>
                      <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
                        <span className="flex items-center gap-2 text-sage-700 font-medium">
                          <Check className="w-4 h-4 text-sage-600" />
                          Laya Healthcare
                        </span>
                        <span className="flex items-center gap-2 text-sage-700 font-medium">
                          <Check className="w-4 h-4 text-sage-600" />
                          Irish Life Health
                        </span>
                        <span className="flex items-center gap-2 text-charcoal/40">
                          <X className="w-4 h-4 text-charcoal/30" />
                          VHI (not covered)
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      )}

      {/* Practitioner Section — Injury & Sports only */}
      {service.slug === 'injury-sports' && (
        <section className="py-16 md:py-20 bg-cream-100" data-testid="practitioner-section" id="practitioners">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <span className="text-xs uppercase tracking-[0.2em] text-sage-500 font-semibold mb-4 block">
                Our Team
              </span>
              <h2 className="font-serif text-3xl md:text-4xl font-light text-charcoal mb-4">
                Choose Your Practitioner
              </h2>
              <p className="text-charcoal/60 text-lg max-w-2xl mx-auto">
                Our team provides hands-on treatment tailored to your needs. Choose your practitioner below.
              </p>
            </motion.div>

            {/* Ali — Lead */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-3xl mx-auto mb-8"
            >
              <Card className="bg-sage-50 border-0 shadow-medium rounded-2xl overflow-hidden">
                <CardContent className="p-8 md:p-10">
                  <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
                    <div className="shrink-0">
                      <div className="w-20 h-20 bg-sage-100 rounded-full overflow-hidden flex items-center justify-center">
                        <img src="/ali-headshot.png" alt="Ali Dorgan" className="w-full h-full object-cover" />
                      </div>
                    </div>
                    <div className="flex-1 text-center sm:text-left">
                      <div className="flex items-center justify-center sm:justify-start gap-2 mb-1">
                        <Star className="w-4 h-4 text-sage-600" />
                        <span className="text-xs uppercase tracking-wider text-sage-600 font-semibold">Lead Practitioner</span>
                      </div>
                      <h3 className="font-serif text-2xl font-medium text-charcoal">Ali Dorgan</h3>
                      <p className="text-sage-600 text-sm font-semibold mb-3">Neuromuscular & Nutritional Therapist</p>
                      <p className="text-charcoal/60 text-sm leading-relaxed mb-3">
                        Experienced in injury treatment, hands-on therapy, and personalised nutrition support.
                      </p>
                      <p className="text-charcoal/50 text-xs italic mb-5" data-testid="ali-high-demand-note">
                        Due to high demand, appointments with Ali are managed directly via WhatsApp.
                      </p>
                      <Button
                        asChild
                        size="lg"
                        className="bg-[#25D366] hover:bg-[#20BD5C] text-white rounded-full gap-2 py-5 px-8 font-semibold hover:-translate-y-0.5 transition-all duration-300"
                        data-testid="practitioner-ali-btn"
                      >
                        <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                          <MessageCircle className="w-5 h-5" />
                          Message Ali on WhatsApp
                        </a>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Brian & Joy */}
            <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
              >
                <Card className="h-full bg-white border-0 shadow-soft hover:shadow-medium transition-all duration-300 rounded-2xl overflow-hidden">
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 bg-sage-100 rounded-full overflow-hidden flex items-center justify-center mx-auto mb-4">
                      <img src="/brian-headshot.png" alt="Brian Lawton" className="w-full h-full object-cover" />
                    </div>
                    <h3 className="font-serif text-xl font-medium text-charcoal mb-1">Brian Lawton</h3>
                    <p className="text-sage-600 text-sm font-semibold mb-2">Physical Therapist</p>
                    <p className="text-xs uppercase tracking-wider text-charcoal/40 font-medium mb-4">Sports injury, rehabilitation, performance support</p>
                    <p className="text-charcoal/60 text-sm leading-relaxed mb-4">
                      Over 25 years of professional practice. Extensive experience with athletes across multiple sports and competitive levels.
                    </p>
                    <p className="text-sage-600 text-xs font-semibold mb-5 flex items-center justify-center gap-1.5" data-testid="brian-booking-available">
                      <Calendar className="w-3.5 h-3.5" />
                      Online booking available
                    </p>
                    <Button
                      asChild
                      className="w-full rounded-full bg-sage-600 hover:bg-sage-500 text-white py-5 font-semibold gap-2 transition-all duration-300 hover:-translate-y-0.5 shadow-sm hover:shadow-md"
                      data-testid="practitioner-brian-btn"
                    >
                      <a href="https://www.fresha.com/book-now/alidorgan-nt-and-nmt-g0b0bzyf/services?lid=206215&eid=611079&oiid=sv%3A11859734&share=true&pId=192546" target="_blank" rel="noopener noreferrer">
                        <Calendar className="w-4 h-4" />
                        Book with Brian
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
              >
                <Card className="h-full bg-white border-0 shadow-soft hover:shadow-medium transition-all duration-300 rounded-2xl overflow-hidden">
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 bg-sage-100 rounded-full overflow-hidden flex items-center justify-center mx-auto mb-4">
                      <img src="/joy-headshot.png" alt="Joy" className="w-full h-full object-cover" />
                    </div>
                    <h3 className="font-serif text-xl font-medium text-charcoal mb-1">Joy</h3>
                    <p className="text-sage-600 text-sm font-semibold mb-2">Neuromuscular Therapist</p>
                    <p className="text-xs uppercase tracking-wider text-charcoal/40 font-medium mb-4">Soft tissue therapy, mobility, yoga-informed treatment</p>
                    <p className="text-charcoal/60 text-sm leading-relaxed mb-4">
                      NTC and HCD trained. Focuses on alleviating soft tissue pain while improving overall function. Also a qualified yoga teacher.
                    </p>
                    <p className="text-sage-600 text-xs font-semibold mb-5 flex items-center justify-center gap-1.5" data-testid="joy-booking-available">
                      <Calendar className="w-3.5 h-3.5" />
                      Online booking available
                    </p>
                    <Button
                      asChild
                      className="w-full rounded-full bg-sage-600 hover:bg-sage-500 text-white py-5 font-semibold gap-2 transition-all duration-300 hover:-translate-y-0.5 shadow-sm hover:shadow-md"
                      data-testid="practitioner-joy-btn"
                    >
                      <a href="https://www.fresha.com/book-now/alidorgan-nt-and-nmt-g0b0bzyf/services?lid=206215&eid=611079&oiid=sv%3A23043269&share=true&pId=192546" target="_blank" rel="noopener noreferrer">
                        <Calendar className="w-4 h-4" />
                        Book with Joy
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* Content Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-16">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                {/* About Section */}
                <div className="mb-16">
                  <h2 className="font-serif text-3xl font-light text-charcoal mb-8">
                    About This Service
                  </h2>
                  <div className="space-y-6">
                    {descriptionParagraphs.map((paragraph, index) => (
                      <p key={index} className="text-charcoal/70 text-lg leading-relaxed">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>

                {/* Benefits Section */}
                {service.benefits && (
                  <div className="mb-16">
                    <h3 className="font-serif text-2xl font-light text-charcoal mb-8">
                      Benefits
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      {service.benefits.map((benefit, index) => (
                        <div 
                          key={index} 
                          className="flex items-start gap-4 bg-cream-50 rounded-xl p-5"
                        >
                          <div className="w-6 h-6 bg-sage-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Check className="w-4 h-4 text-sage-600" />
                          </div>
                          <span className="text-charcoal/80">{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Conditions/Topics Section */}
                {(service.conditions || service.topics) && (
                  <div>
                    <h3 className="font-serif text-2xl font-light text-charcoal mb-8">
                      {service.conditions ? 'Conditions Treated' : 'Topics Covered'}
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      {(service.conditions || service.topics).map((item, index) => (
                        <div 
                          key={index} 
                          className="flex items-start gap-4 bg-sage-50 rounded-xl p-5"
                        >
                          <div className="w-6 h-6 bg-sage-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Check className="w-4 h-4 text-sage-700" />
                          </div>
                          <span className="text-charcoal/80">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="sticky top-28 space-y-8"
              >
                {/* Pricing Card */}
                <Card className="bg-sage-50 border-0 shadow-soft rounded-2xl overflow-hidden">
                  <CardContent className="p-8">
                    <h3 className="font-serif text-2xl font-light text-charcoal mb-6">
                      Pricing
                    </h3>
                    <div className="space-y-4">
                      {service.pricing.map((item, index) => (
                        <div 
                          key={index} 
                          className="flex items-center justify-between gap-4 py-4 border-b border-sage-200 last:border-0"
                        >
                          <span className="text-charcoal/70 min-w-0">{item.name}</span>
                          <span className="font-serif text-2xl text-sage-600 font-medium whitespace-nowrap shrink-0">{item.price}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Sidebar CTA removed — top and bottom CTAs handle conversion */}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-sage-900" data-testid="service-cta-bottom">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h3 className="font-serif text-2xl md:text-3xl font-light text-cream-100 mb-4">
              {service.slug === 'nutritional-therapy' ? 'Ready to Apply?' : 'Ready to Get Started?'}
            </h3>
            <p className="text-cream-200/70 text-lg mb-8 max-w-xl mx-auto">
              {service.slug === 'nutritional-therapy'
                ? 'Complete the pre-consultation questionnaire or get in touch directly.'
                : 'Book an appointment or message Ali to discuss your needs.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {service.slug === 'nutritional-therapy' ? (
                <Button
                  asChild
                  size="lg"
                  className="bg-sage-600 hover:bg-sage-700 text-white rounded-full gap-2 py-6 px-10 font-semibold hover:-translate-y-0.5 transition-all duration-300"
                >
                  <Link to="/services/nutritional-therapy/apply">
                    <ClipboardList className="w-5 h-5" />
                    Apply for Nutritional Therapy
                  </Link>
                </Button>
              ) : (
                <Button
                  asChild
                  size="lg"
                  className="bg-white text-sage-900 hover:bg-cream-100 rounded-full gap-2 py-6 px-10 font-semibold hover:-translate-y-0.5 transition-all duration-300"
                >
                  <Link to="/services/injury-sports#practitioners">
                    <Calendar className="w-5 h-5" />
                    Book Injury & Sports Treatment
                  </Link>
                </Button>
              )}
              <Button
                asChild
                size="lg"
                variant="outline"
                className="rounded-full border-2 border-cream-100 text-cream-100 hover:bg-cream-100 hover:text-sage-900 py-6 px-10 font-semibold transition-all duration-300"
              >
                <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Message Ali
                </a>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Other Services */}
      <section className="py-20 md:py-28 bg-cream-100">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-3xl md:text-4xl font-light text-charcoal">
              Other Services
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {services
              .filter(s => s.slug !== slug)
              .map((otherService, index) => (
                <motion.div
                  key={otherService.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link to={`/services/${otherService.slug}`}>
                    <Card className="h-full bg-white border-0 shadow-soft hover:shadow-medium hover:-translate-y-2 transition-all duration-300 rounded-2xl overflow-hidden group">
                      <CardContent className="p-8">
                        <h3 className="font-serif text-xl font-medium text-charcoal mb-3 group-hover:text-sage-600 transition-colors">
                          {otherService.title}
                        </h3>
                        <p className="text-charcoal/60 text-sm mb-4 leading-relaxed">
                          {otherService.shortDescription}
                        </p>
                        <span className="inline-flex items-center gap-1 text-sage-600 font-semibold text-sm">
                          Learn More
                          <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </span>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicePage;
